import type { Session, SessionSummary } from "../types/core";
import { normalizeMarkdownHeadings } from "../utils/markdown";

/**
 * Options for Markdown export.
 */
interface MarkdownExportOptions {
	includeTranscript?: boolean;
	includeMetadata?: boolean;
}

/**
 * Utility to convert SessionSummary data into Markdown/JSON and handle file downloads.
 */
export class MarkdownExporter {
	/**
	 * Converts a SessionSummary object to a formatted Markdown string.
	 * @param summary The summary to convert
	 * @param transcript Optional full transcript to include
	 * @param options Export configuration
	 * @returns Markdown string
	 */
	convertToMarkdown(
		summary: SessionSummary,
		transcript?: string,
		options: MarkdownExportOptions = {},
	): string {
		const { includeTranscript = true } = options;
		const dateStr = summary.generatedAt.toLocaleString();

		// Use title if available, otherwise fallback to "Session Summary"
		const title = summary.title || "セッション要約";
		let md = `# ${title} - ${dateStr}\n\n`;

		// Section: Details (H2)
		md += "## セッション情報\n\n";
		md += `**セッションID:** \`${summary.sessionId}\`  \n`;
		md += `**生成日時:** ${dateStr}\n\n`;

		// Section: Summary (H2)
		// Normalize headings so the highest level starts at H3, fitting perfectly under the H2 "Summary" section.
		md += "## 要約\n\n";
		md += `${normalizeMarkdownHeadings(summary.overallSummary, 3)}\n\n`;

		if (includeTranscript) {
			const transcriptToUse = (
				transcript || summary.formattedTranscript
			)?.replace(/`/g, "\\` ");

			if (transcriptToUse) {
				// Section: Full Transcript (H2)
				md += "## 文字起こし\n\n";
				if (transcript || summary.formattedTranscript) {
					// Normalize headings to H3 for consistency
					md += normalizeMarkdownHeadings(transcriptToUse, 3);
					md += "\n";
				} else {
					// Legacy raw transcript, wrap in code block
					md += "```text\n";
					md += transcriptToUse;
					md += "\n```\n";
				}
			}
		}

		md += "\n---\n*Generated by MONOLOG LIVE*";

		return md;
	}

	/**
	 * Triggers a browser download of the summary as a Markdown file.
	 * @param summary The summary to export
	 * @param transcript Optional transcript to include
	 * @param options Export configuration
	 */
	downloadAsFile(
		summary: SessionSummary,
		transcript?: string,
		options?: MarkdownExportOptions,
	): void {
		const markdown = this.convertToMarkdown(summary, transcript, options);
		const blob = new Blob([markdown], { type: "text/markdown" });

		// Use different prefix based on whether transcript is included
		const includeTranscript = options?.includeTranscript !== false;
		const prefix = includeTranscript ? "session-full" : "session-summary";

		this.triggerDownload(
			blob,
			`${prefix}-${summary.sessionId}-${new Date().toISOString().split("T")[0]}.md`,
		);
	}

	/**
	 * Triggers a browser download of the session and summary as a JSON file.
	 * @param session The session data
	 * @param summary The summary data
	 */
	downloadAsJson(session: Session, summary: SessionSummary | null): void {
		const data = {
			exportVersion: "1.0",
			exportDate: new Date().toISOString(),
			session: {
				id: session.id,
				title: session.title,
				startTime: session.startTime,
				endTime: session.endTime,
				transcript: session.transcript,
				metrics: session.metrics,
				isFavorite: session.isFavorite,
			},
			summary: summary
				? {
						overallSummary: summary.overallSummary,
						generatedAt: summary.generatedAt,
						topics: summary.topics,
					}
				: null,
		};

		const json = JSON.stringify(data, null, 2);
		const blob = new Blob([json], { type: "application/json" });
		this.triggerDownload(
			blob,
			`session-data-${session.id}-${new Date().toISOString().split("T")[0]}.json`,
		);
	}

	/**
	 * Internal helper to trigger a file download.
	 */
	private triggerDownload(blob: Blob, filename: string): void {
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = filename;
		document.body.appendChild(link);
		link.click();

		// Cleanup
		setTimeout(() => {
			document.body.removeChild(link);
			URL.revokeObjectURL(url);
		}, 100);
	}
}
